import {
  ChatContainer,
  InputAreaContainer
} from "./styles";
import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createSession, updateSession, deleteSessionMessages } from "../../services/session";
import {
  AlertCircle,
  Beaker,
  Check,
  Copy,
  File as FileIcon,
  Mic,
  Paperclip,
  RotateCcw,
  Send,
  Settings2,
  Trash2,
  X,
} from "lucide-react";

import { useVoiceVisualizer, VoiceVisualizer } from 'react-voice-visualizer';
import { SettingsModal } from "./SettingsModal";
import { Messages } from "./Messages";
import { useGenerativeSessionSeed } from "../../context/GenerativeSessionContext";
import { toast } from 'sonner';
import { Button } from "../ui/button";
import { Select } from "../ui/select";
import { Tooltip } from "../ui/tooltip";

const mapSessionToChatMessages = (conversation) => {
  const triggerStore = {};
  const messages = [];

  conversation.forEach(item => {
    switch (item.role) {
      case 'human':
        const humanResponseParts = [];
        item.content.forEach(c => {
          if (c.text) {
            humanResponseParts.push({ type: 'text', data: c.text, id: item.id });
          }
          if (c.contentFileURLs) {
            c.contentFileURLs.forEach(file => {
              humanResponseParts.push({ type: 'image', data: file.url, id: item.id });
            });
          }
        });

        if (humanResponseParts.length > 0) {
          messages.push({
            isMachine: false,
            data: {
              response: humanResponseParts
            }
          });
        }
        break;

      case 'ai':
        const aiResponseParts = [];
        item.content.forEach(c => {
          if (c.type === 'function') {
            const args = JSON.parse(c.arguments);
            const triggerData = {
              function_name: c.name,
              args,
              actions: []
            };
            triggerStore[item.id] = triggerData;
            aiResponseParts.push({
              type: 'trigger',
              id: item.id,
              data: { ...triggerData, _callId: c.id }
            });
          } else if (c.text) {
            aiResponseParts.push({ type: 'text', data: c.text, id: item.id });
          }
        });

        if (aiResponseParts.length > 0) {
          messages.push({
            isMachine: true,
            data: {
              response: aiResponseParts
            }
          });
        }
        break;

      case 'tool':
        item.content.forEach(c => {
          const callId = c.tool_call_id;
          const associatedTriggerMessage = messages.find(msg =>
            msg.data.response.some(part => part.type === 'trigger' && part.data._callId === callId)
          );

          if (associatedTriggerMessage) {
            const triggerPart = associatedTriggerMessage.data.response.find(part => part.type === 'trigger' && part.data._callId === callId);
            const m = /"""([\s\S]*?)"""/.exec(c.content);
            let payload = [];
            if (m) {
              try { payload = JSON.parse(m[1]); }
              catch (e) { console.warn('JSON inválido em tool.content', e); }
            }
            const statusMatch = /StatusCode:\s*(\d+)/.exec(c.content);
            const status_code = statusMatch ? +statusMatch[1] : null;

            triggerPart.data.actions.push({ status_code, payload });
          }
        });
        break;

      default:
        break;
    }
  });

  messages.forEach(msg => {
    msg.data.response.forEach(part => {
      if (part.type === 'trigger') {
        delete part.data._callId;
      }
    });
  });

  return messages;
};

export default function GenerativeChatTest({ assistantId }) {
  const [text, setText] = useState('')
  const [messages, setMessages] = useState([]);
  const [session, setSession] = useState('');
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [failedSession, setFailedSession] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [actionLoading, setActionLoading] = useState({ type: null, index: null, id: null });
  const [waitingMessage, setWaitingMessage] = useState(false)

  const [audioDisabled, setAudioDisabled] = useState(false);

  const inputRef = useRef();
  const recorderControls = useVoiceVisualizer();

  const { generativeSessionSeedId, setGenerativeSessionSeedId, setLoadingSeedId } = useGenerativeSessionSeed()

  const [channel, setChannel] = useState(() => {
    const item = localStorage.getItem("channel")
    return item ? item : 'API';
  })

  const [sessionSettings, setSessionSettings] = useState(() => {
    const item = localStorage.getItem("sessionSettings");
    return item ? JSON.parse(item) : {
      ignore_trigger_response: false,
      force_trigger_call: false,
    };
  });

  const [clientData, setClientData] = useState(() => {
    if (session.client_data && Object.keys(session.client_data).length > 0) {
      return session.client_data;
    }

    const item = localStorage.getItem("clientData");
    return item ? JSON.parse(item) : {};
  })

  const {
    startRecording,
    stopRecording,
    clearCanvas,
    isRecordingInProgress,
    isAvailableRecordedAudio,
    recordingTime,
    recordedBlob,
    audioSrc,
    duration
  } = recorderControls;

  const recorderProps = useMemo(() => ({
    height: 0,
    width: '0',
    barWidth: 0,
    isControlPanelShown: false,
    isDefaultUIShown: false,
    isProgressIndicatorShown: false,
    isAudioProcessingTextShown: false,
  }), []);

  const getFilesObjects = useCallback((fileList) => {
    return fileList.map((item) => {
      return { data: item.name, type: "file" }
    }) || []
  }, [])

  const newMessage = useCallback(async (text, fileList) => {
    setWaitingMessage(true)
    const tempText = text;
    setText('')
    setMessages(m => [...m, {
      data: {
        response: [
          {
            data: tempText,
            type: "text"
          },
          ...(fileList?.length > 0 ? getFilesObjects(fileList) : [])
        ]
      }
    }]);

    try {
      let activeSession = session;
      if (!activeSession?.id) {
        const { data: created } = await createSession(
          assistantId,
          assistantId,
          true,
          generativeSessionSeedId
        );
        activeSession = created;
        setSession(created);
        setFailedSession('');
        setMessages(m => {
          if (m.length === 0) return m;
          const last = m[m.length - 1];
          const before = m.slice(0, -1);
          return [
            ...before,
            {
              isSystem: true,
              data: { response: [{ data: 'Nova sessão criada', type: 'text' }] }
            },
            last,
          ];
        });
      }

      const result = await updateSession(
        activeSession.id,
        tempText,
        true,
        fileList,
        true,
        clientData,
        channel,
        sessionSettings
      );
      setMessages(m => [...m, { data: result, isMachine: true }]);
    } catch (err) {
      const errData = err?.data || err;
      console.error(errData);
      setMessages(m => [...m, {
        data: { response: [{ data: 'Falha interna: ' + (errData?.message || 'erro desconhecido'), type: 'text' }] },
        isMachine: true,
        isError: true
      }]);
    } finally {
      setWaitingMessage(false);
      setFileList([]);
    }
  }, [getFilesObjects, session, assistantId, generativeSessionSeedId, clientData, channel, sessionSettings])

  const loadSeedSession = useCallback(() => {
    setLoading(true);
    setLoadingSeedId(generativeSessionSeedId);
    createSession(assistantId, assistantId, true, generativeSessionSeedId).then(({ data }) => {
      setSession(data);
      if (data.current_conversation) {
        toast.success("Sessão carregada para teste!");
        const initialMessages = mapSessionToChatMessages(data.current_conversation);
        setMessages([
          {
            isSystem: true,
            data: { response: [{ data: 'Sessão carregada do histórico', type: 'text' }] }
          },
          ...initialMessages,
        ]);
        setFailedSession('');
        return;
      }
      setMessages([]);
      setFailedSession('');
    }).catch(e => {
      toast.error("Houve um erro ao carregar sessão");
      setFailedSession(e?.message || 'Falha ao carregar sessão');
    }).finally(() => {
      setLoading(false);
      setLoadingSeedId(null);
    });
  }, [assistantId, generativeSessionSeedId, setLoadingSeedId]);

  const newSession = useCallback(() => {
    setSession('');
    setMessages([]);
    setFailedSession('');
  }, []);

  useEffect(() => {
    if (generativeSessionSeedId) {
      loadSeedSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generativeSessionSeedId])

  useEffect(() => {
    if (isAvailableRecordedAudio && recordedBlob) {
      const fileName = 'audio.webm';
      const fileOptions = { type: recordedBlob.type, lastModified: Date.now() };
      const newAudioFile = new File([recordedBlob], fileName, fileOptions);

      const audioObjectForMessage = {
        data: newAudioFile.name,
        type: "audio",
        url: audioSrc,
        audioDuration: duration
      };

      setMessages(m => [...m, {
        data: {
          response: [
            { data: text, type: "text" },
            audioObjectForMessage
          ]
        }
      }]);

      updateSession(
        session.id,
        text,
        true,
        [newAudioFile],
        true,
        clientData,
        channel,
        sessionSettings
      ).then((data) => {
        setMessages(m => [...m, { data: data, isMachine: true }]);
      }).catch(({ data }) => {
        console.error(data);
        setMessages(m => [...m, {
          data: { response: [{ data: 'Falha interna: ' + data?.message, type: 'text' }] },
          isMachine: true,
          isError: true
        }]);
      });

      setText('');
      setFileList([]);
      clearCanvas();
    }
  }, [isAvailableRecordedAudio, recordedBlob, audioSrc, duration]);

  const handleRecordAudio = () => {
    if (isRecordingInProgress) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleDeleteAudio = useCallback(() => {
    clearCanvas();
  }, [clearCanvas]);

  const Counter = () => {
    return (
      <div className="flex items-center gap-1 text-xs font-medium" style={{ color: '#d61f3f' }}>
        {Math.round(recordingTime / 1000)}s
        <Mic size={11} />
      </div>
    )
  };

  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) setFileList(prev => [...prev, ...files]);
    e.target.value = '';
  };

  const removeFile = (file) => {
    setFileList(prev => prev.filter(f => f !== file));
  };

  useEffect(() => {
    setAudioDisabled(false);
    if (fileList.length === 0) return;
    if (fileList[0].type !== "audio/webm;codecs=opus") {
      setAudioDisabled(true);
      return;
    }
  }, [fileList]);

  const channelOptions = [
    { value: 'WHATSAPP', label: 'Whatsapp' },
    { value: 'API', label: 'Verboo' },
  ];

  const handleDeleteMessage = async (indexToDelete) => {
    setActionLoading({ type: 'delete', index: indexToDelete });
    const messageParts = messages[indexToDelete]?.data?.response;
    if (!messageParts) {
      setActionLoading({ type: null, index: null });
      return;
    }

    const idsToDelete = messageParts.map(part => part.id).filter(Boolean);

    try {
      if (idsToDelete.length > 0) {
        await deleteSessionMessages(session?.id, { ids: idsToDelete });
      }
      setMessages(prev => prev.filter((_, i) => i !== indexToDelete));
    } catch (error) {
      toast.error("Houve um erro ao deletar a mensagem");
      console.error("Failed to delete message:", error);
    } finally {
      setActionLoading({ type: null, index: null });
    }
  };

  const handleEditMessage = async (messageId, newText) => {
    const messageIndex = messages.findIndex(m => m.data.response.some(part => part.id === messageId));
    if (messageIndex === -1) return;

    setActionLoading({ type: 'edit', id: messageId });

    const idsToDelete = messages.slice(messageIndex).flatMap(m => m.data.response.map(part => part.id)).filter(Boolean);

    try {
      if (idsToDelete.length > 0) {
        await deleteSessionMessages(session?.id, { ids: idsToDelete });
      }
      setMessages(prev => prev.slice(0, messageIndex));
      newMessage(newText, []);
    } catch (error) {
      toast.error("Houve um erro ao editar a mensagem");
      console.error("Failed to edit message:", error);
    } finally {
      setActionLoading({ type: null, id: null });
    }
  };

  const handleRetryMessage = async (messageId) => {
    const messageIndex = messages.findIndex(m => m.data.response.some(part => part.id === messageId));
    if (messageIndex === -1) return;

    setActionLoading({ type: 'retry', id: messageId });
    const messageToRetry = messages[messageIndex];
    const textToRetry = messageToRetry.data.response.find(r => r.type === 'text')?.data || '';

    const idsToDelete = messages.slice(messageIndex).flatMap(m => m.data.response.map(part => part.id)).filter(Boolean);

    try {
      if (idsToDelete.length > 0) {
        await deleteSessionMessages(session?.id, { ids: idsToDelete });
      }
      setMessages(prev => prev.slice(0, messageIndex));
      newMessage(textToRetry, []);
    } catch (error) {
      toast.error("Houve um erro ao tentar mensagem novamente");
      console.error("Failed to retry message:", error);
    } finally {
      setActionLoading({ type: null, id: null });
    }
  };

  const [isCopied, setIsCopied] = useState(false);
  const handleCopy = useCallback(() => {
    if (generativeSessionSeedId) {
      navigator.clipboard.writeText(generativeSessionSeedId).then(() => {
        setIsCopied(true);
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      });
    }
  }, [generativeSessionSeedId]);
  useEffect(() => {
    localStorage.setItem("sessionSettings", JSON.stringify(sessionSettings));
  }, [sessionSettings]);

  useEffect(() => {
    if (clientData && Object.keys(clientData).length > 0) {
      localStorage.setItem("clientData", JSON.stringify(clientData));
    }
  }, [clientData]);

  return (
    <div className="flex h-full w-full flex-col gap-3">
      <SettingsModal
        isOpen={isModalOpen}
        handleOk={(allSettings) => {
          const { clientData, sessionSettings } = allSettings;
          setClientData(clientData);
          setSessionSettings(sessionSettings);
          setIsModalOpen(false);
        }}
        handleCancel={() => setIsModalOpen(false)}
        clientData={clientData}
        sessionSettings={sessionSettings}
      />

      <div
        className="flex shrink-0 items-center gap-2 rounded-xl p-2"
        style={{
          background: 'var(--dash-surface-subtle)',
          border: '1px solid var(--dash-border)',
        }}
      >
        <Tooltip content="Configurações da sessão">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setIsModalOpen(true)}
          >
            <Settings2 size={14} />
          </Button>
        </Tooltip>
        <div className="min-w-[140px] flex-1">
          <Select
            value={channel}
            onChange={(value) => {
              setChannel(value);
              localStorage.setItem('channel', value);
            }}
            options={channelOptions}
            placeholder="Canal"
          />
        </div>
        <Tooltip content="Limpar sessão">
          <Button
            variant="outline"
            size="sm"
            leftIcon={RotateCcw}
            onClick={newSession}
            loading={loading}
            disabled={loading}
          >
            Limpar
          </Button>
        </Tooltip>
      </div>

      {generativeSessionSeedId && (
        <div
          className="flex items-center justify-between gap-3 rounded-xl px-3 py-2"
          style={{
            background: 'var(--dash-primary-soft)',
            border: '1px solid var(--dash-primary-border)',
          }}
        >
          <div className="flex min-w-0 items-center gap-2">
            <Beaker size={14} style={{ color: 'var(--dash-primary)' }} />
            <span
              className="text-[10px] font-medium uppercase tracking-[0.1em]"
              style={{ color: 'var(--dash-primary)' }}
            >
              Sessão semeada:
            </span>
            <span
              className="truncate font-mono text-[11px]"
              style={{ color: 'var(--dash-text-soft)' }}
            >
              {generativeSessionSeedId}
            </span>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Tooltip content={isCopied ? 'Copiado!' : 'Copiar ID'}>
              <Button variant="ghost" size="icon-sm" onClick={handleCopy}>
                {isCopied ? <Check size={12} /> : <Copy size={12} />}
              </Button>
            </Tooltip>
            <Tooltip content="Remover sessão">
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setGenerativeSessionSeedId('')}
              >
                <X size={12} />
              </Button>
            </Tooltip>
          </div>
        </div>
      )}

      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl"
        style={{
          background: 'var(--dash-surface)',
          border: '1px solid var(--dash-border)',
        }}
      >
        <ChatContainer
          colorPrimaryBg={'var(--dash-primary-border)'}
          borderRadius={12}
          colorBg={'var(--dash-surface)'}
        >
          <Messages
            waitingMessage={waitingMessage}
            messages={messages}
            newMessage={newMessage}
            onRetry={handleRetryMessage}
            onEdit={handleEditMessage}
            onDelete={handleDeleteMessage}
            actionLoading={actionLoading}
          />
        </ChatContainer>

        <InputAreaContainer>
          {failedSession && (
            <div
              className="mb-3 flex items-start gap-2 rounded-lg p-3 text-xs"
              style={{
                background: 'rgba(254,52,86,0.10)',
                border: '1px solid rgba(254,52,86,0.25)',
                color: '#d61f3f',
              }}
            >
              <AlertCircle size={14} className="mt-0.5 shrink-0" />
              <span>{failedSession}</span>
            </div>
          )}

          {fileList.some((file) => !file.type.startsWith('audio/')) && (
            <div className="mb-3 flex flex-col gap-1">
              {fileList.map((file) => {
                if (file.type.startsWith('audio/')) return null;
                return (
                  <div
                    key={file.uid || file.name}
                    className="flex items-center justify-between rounded-lg px-3 py-2"
                    style={{
                      background: 'var(--dash-surface-subtle)',
                      border: '1px solid var(--dash-border)',
                    }}
                  >
                    <div
                      className="flex min-w-0 items-center gap-2 text-xs"
                      style={{ color: 'var(--dash-text-soft)' }}
                    >
                      <FileIcon size={12} className="shrink-0" />
                      <span className="truncate">{file?.name}</span>
                    </div>
                    <Button
                      variant="danger"
                      size="icon-sm"
                      onClick={() => removeFile(file)}
                    >
                      <Trash2 size={12} />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />

          <div className="flex items-end gap-2">
            {isRecordingInProgress ? (
              <Button
                variant="danger"
                size="icon"
                onClick={handleDeleteAudio}
              >
                <Trash2 size={14} />
              </Button>
            ) : (
              <Button
                variant="outline"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip size={14} />
              </Button>
            )}

            <textarea
              ref={inputRef}
              rows={1}
              disabled={loading || failedSession || isRecordingInProgress}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  newMessage(text, fileList);
                }
              }}
              value={text}
              onChange={({ target }) => setText(target.value)}
              placeholder="Digite aqui..."
              className="flex min-h-10 flex-1 resize-none rounded-xl px-4 py-2.5 text-sm outline-none transition-all duration-200 placeholder:text-[var(--dash-text-faint)] disabled:opacity-50"
              style={{
                background: 'var(--dash-surface-subtle)',
                color: 'var(--dash-text)',
                border: '1px solid var(--dash-border)',
                fontFamily: 'inherit',
                maxHeight: 144,
              }}
            />

            {isRecordingInProgress && <Counter />}

            <Button
              variant={text.trim() ? 'primary' : 'outline'}
              size="icon"
              disabled={audioDisabled}
              onClick={() => {
                if (text.trim()) {
                  newMessage(text, fileList);
                } else {
                  handleRecordAudio();
                }
              }}
            >
              {text.trim() ? (
                <Send size={14} />
              ) : isRecordingInProgress ? (
                <Send size={14} />
              ) : (
                <Mic size={14} />
              )}
            </Button>
          </div>
          <VoiceVisualizer controls={recorderControls} {...recorderProps} />
        </InputAreaContainer>
      </div>
    </div>
  );
}
